import { useState, useEffect, useCallback, useRef } from 'react';
import { ref, onValue, Unsubscribe } from 'firebase/database';
import { database } from '../firebase/firebase';
import { BankInfo } from '../types';

export interface EventUser {
    displayName: string;
    email?: string;
    createdAt: number;
    lastAccess: number;
    bankInfo?: BankInfo;
    isAnonymous?: boolean;
    userId?: string;
    anonymousId?: string;
}

interface UseEventUsersCache {
    [eventId: string]: {
        [userId: string]: EventUser;
    };
}

interface ListenerEntry {
    unsubscribe: Unsubscribe;
    // Subscribers are tracked per event: a global set meant unmounting a
    // consumer of event A could never release event B's listener (and usually
    // released nothing at all).
    subscribers: Set<() => void>;
    loaded: boolean;
}

const eventUsersCache: UseEventUsersCache = {};
const listeners = new Map<string, ListenerEntry>();

// One shared empty object, so consumers memoising on `eventUsers` don't see a
// brand new identity on every render while the listener is still loading.
const EMPTY_EVENT_USERS: { [userId: string]: EventUser } = {};

const notify = (eventId: string) => {
    listeners.get(eventId)?.subscribers.forEach(callback => callback());
};

export const useEventUsersCache = (eventId?: string) => {
    const [, setUpdateCount] = useState(0);
    const [loading, setLoading] = useState(() =>
        // Only the very first consumer of an event waits: for everyone else the
        // cache is already populated.
        Boolean(eventId) && !listeners.get(eventId!)?.loaded
    );
    const [error, setError] = useState<string | null>(null);

    // A stable identity, so the subscriber added on mount is the exact one
    // removed on unmount. Reassigning a fresh closure every render used to leak
    // every previous callback into the set forever.
    const forceUpdate = useRef(() => {
        setUpdateCount(prev => prev + 1);
        setLoading(false);
    }).current;

    useEffect(() => {
        if (!eventId) return;

        let entry = listeners.get(eventId);

        if (!entry) {
            const eventUsersRef = ref(database, `eventUsers/${eventId}`);
            const newEntry: ListenerEntry = {
                subscribers: new Set(),
                loaded: false,
                unsubscribe: onValue(
                    eventUsersRef,
                    (snapshot) => {
                        eventUsersCache[eventId] = snapshot.val() || {};
                        newEntry.loaded = true;
                        notify(eventId);
                    },
                    (err) => {
                        console.error('Firebase eventUsers error:', err);
                        newEntry.loaded = true;
                        setError('Failed to load event users');
                        notify(eventId);
                    }
                ),
            };
            listeners.set(eventId, newEntry);
            entry = newEntry;
        }

        entry.subscribers.add(forceUpdate);
        if (entry.loaded) setLoading(false);

        return () => {
            const current = listeners.get(eventId);
            if (!current) return;

            current.subscribers.delete(forceUpdate);

            // Last consumer of *this* event leaves — tear down its listener only.
            if (current.subscribers.size === 0) {
                current.unsubscribe();
                listeners.delete(eventId);
                delete eventUsersCache[eventId];
            }
        };
    }, [eventId, forceUpdate]);

    const getEventUser = useCallback(
        (id: string, userId: string): EventUser | null => eventUsersCache[id]?.[userId] || null,
        []
    );

    const getEventUserDisplayNames = useCallback(
        (id: string, userIds: string[]): { [userId: string]: string } => {
            const names: { [userId: string]: string } = {};
            userIds.forEach(userId => {
                names[userId] = eventUsersCache[id]?.[userId]?.displayName || userId;
            });
            return names;
        },
        []
    );

    return {
        loading,
        error,
        getEventUser,
        getEventUserDisplayNames,
        eventUsers: (eventId && eventUsersCache[eventId]) || EMPTY_EVENT_USERS
    };
};
